import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { uploadFoodoraInvoice } from '../utils/foodoraInvoiceUpload';

const ACCEPT = '.pdf';
const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;
const PAGE_SIZE = 10;
const STORAGE_BUCKET = 'foodora_invoices';

function formatDate(d) {
  if (!d) return '–';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '–' : date.toLocaleDateString('nb-NO', { dateStyle: 'medium' });
}

const FoodoraUploadPage = () => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [uploadsTotal, setUploadsTotal] = useState(0);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [viewingFile, setViewingFile] = useState(null);
  const [geminiModalRow, setGeminiModalRow] = useState(null);
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);
  const inputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(uploadsTotal / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const canPrev = currentPage > 0;
  const canNext = currentPage < totalPages - 1;

  const loadUploads = async (pageNum = 0) => {
    setUploadsLoading(true);
    const from = pageNum * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const { data, count, error } = await supabase
      .from('foodora_invoices')
      .select('id, created_at, file, status, gemini_response, reviewed_response', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);
    if (error) { setUploads([]); setUploadsTotal(0); }
    else { setUploads(data || []); setUploadsTotal(count ?? 0); }
    setUploadsLoading(false);
  };

  useEffect(() => { loadUploads(currentPage); }, [currentPage]);

  const acceptFiles = (fileList) => {
    if (!fileList?.length) return;
    setValidationError(null);
    const valid = [];
    const errors = [];
    for (let i = 0; i < fileList.length; i++) {
      const f = fileList[i];
      const ext = (f.name || '').toLowerCase();
      const isPdf = f.type === 'application/pdf' || /\.pdf$/.test(ext) || (f.type === '' && /\.pdf$/.test(ext));
      if (!isPdf) { errors.push(`${f.name}: not a PDF`); continue; }
      if (f.size > MAX_SIZE) { errors.push(`${f.name}: larger than ${MAX_SIZE_MB}MB`); continue; }
      valid.push(f);
    }
    if (errors.length > 0) setValidationError(errors.join('. '));
    if (valid.length > 0) setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (index) => { setFiles((prev) => prev.filter((_, i) => i !== index)); setValidationError(null); };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const fl = e.dataTransfer?.files; if (fl?.length) acceptFiles(Array.from(fl)); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onInputChange = (e) => { const fl = e.target?.files; if (fl?.length) acceptFiles(Array.from(fl)); e.target.value = ''; };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setIsLoading(true); setUploadError(null); setUploadSuccess(null);
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const res = await uploadFoodoraInvoice(files[i]);
      results.push({ name: files[i].name, ...res });
    }
    setIsLoading(false);
    const succeeded = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);
    if (succeeded.length > 0) {
      setUploadSuccess(succeeded.length === results.length ? `${succeeded.length} file(s) uploaded.` : `${succeeded.length} of ${results.length} uploaded. ${failed.length} failed.`);
      setFiles([]); setValidationError(null); loadUploads(0); setPage(0);
    }
    if (failed.length > 0) setUploadError(failed.map((r) => `${r.name}: ${r.error}`).join('. '));
  };

  const clearSuccess = () => { setUploadSuccess(null); setUploadError(null); };

  const handleViewFile = async (filePath) => {
    if (!filePath) return;
    setViewingFile(filePath);
    try {
      const { data, error } = await supabase.storage.from(STORAGE_BUCKET).createSignedUrl(filePath, 3600);
      if (error) throw error;
      if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to get signed URL:', err);
      setUploadError(err.message || 'Could not open file');
    } finally { setViewingFile(null); }
  };

  const openReviewModal = (row) => {
    const g = row.gemini_response || {};
    const lines = Array.isArray(g.lines) ? g.lines : [];
    const getAmount = (desc) => { const line = lines.find((l) => l?.description === desc); return line?.gross_amount ?? 0; };
    setReviewForm({ date: g.date || '', organization_name: g.organization_name || '', organization_number: g.organization_number ?? '', store_name: g.store_name || '', invoice_number: g.invoice_number ?? '', revenue: getAmount('Revenue'), fees: getAmount('Fees'), payout: getAmount('Payout'), round_off: getAmount('Round-off') });
    setApproveError(null);
    setReviewRow(row);
  };

  const closeReviewModal = () => { setReviewRow(null); setReviewForm(null); setApproveError(null); };

  const handleRowClick = (row) => {
    if (row.gemini_response == null && row.status !== 'needs_review') return;
    if (row.status === 'needs_review') openReviewModal(row);
    else setGeminiModalRow(row);
  };

  const reviewBalance = reviewForm ? Number(reviewForm.revenue || 0) + Number(reviewForm.fees || 0) + Number(reviewForm.payout || 0) + Number(reviewForm.round_off || 0) : 0;
  const reviewBalances = Math.abs(reviewBalance) < 0.001;

  const handleApprove = async () => {
    if (!reviewRow || !reviewForm) return;
    setApproving(true); setApproveError(null);
    const payload = {
      invoice_id: reviewRow.id, date: reviewForm.date, organization_name: reviewForm.organization_name, organization_number: reviewForm.organization_number, store_name: reviewForm.store_name, invoice_number: reviewForm.invoice_number,
      lines: [
        { description: 'Revenue', gross_amount: Number(reviewForm.revenue) || 0 },
        { description: 'Fees', gross_amount: Number(reviewForm.fees) || 0 },
        { description: 'Payout', gross_amount: Number(reviewForm.payout) || 0 },
        { description: 'Round-off', gross_amount: Number(reviewForm.round_off) || 0 },
      ],
    };
    try {
      const { data, error } = await supabase.functions.invoke('foodora-invoice-approve', { body: payload });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      closeReviewModal(); loadUploads(currentPage);
    } catch (err) { setApproveError(err.message || 'Approval failed'); }
    finally { setApproving(false); }
  };

  const updateReviewField = (field, value) => { setReviewForm((prev) => ({ ...prev, [field]: value })); };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-600',
      ok: 'bg-gray-100 text-black',
      needs_review: 'bg-gray-200 text-black',
      'edge function failed': 'bg-red-50 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-xl font-semibold text-black">Foodora invoice upload</h1>
          <p className="mt-1 text-sm text-gray-400">Upload Foodora invoice PDFs.</p>
        </header>

        {uploadSuccess && (
          <div className="border border-gray-200 rounded-md p-6 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-4">{uploadSuccess}</p>
            <button type="button" onClick={clearSuccess} className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-900 transition-colors">Upload more files</button>
          </div>
        )}

        {!uploadSuccess && (
          <div className="border border-gray-200 rounded-md p-5 mb-6">
            <label className="block text-xs font-medium text-gray-500 mb-2">Files</label>
            <div
              role="button" tabIndex={0} onDrop={onDrop} onDragOver={onDragOver} onDragEnter={onDragOver} onDragLeave={onDragLeave}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
              className={`border border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-gray-400 bg-gray-50' : 'border-gray-300 hover:border-gray-400'}`}
            >
              <input ref={inputRef} type="file" accept={ACCEPT} multiple onChange={onInputChange} disabled={isLoading} className="hidden" />
              {isLoading ? <p className="text-sm text-gray-500">Uploading...</p> : files.length > 0 ? <p className="text-sm text-black font-medium">{files.length} PDF(s) selected</p> : <p className="text-sm text-gray-400">Click or drag and drop PDF files (multiple allowed)</p>}
            </div>

            {files.length > 0 && !isLoading && (
              <div className="mt-3 border border-gray-200 rounded-md p-3">
                <p className="text-xs font-medium text-gray-500 mb-2">Selected files</p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate flex-1 min-w-0">{f.name}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="ml-2 text-gray-400 hover:text-red-600 shrink-0" aria-label={`Remove ${f.name}`}>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {validationError && <p className="mt-3 text-sm text-red-600">{validationError}</p>}

            {files.length > 0 && !isLoading && (
              <div className="mt-4 flex justify-end">
                <button type="button" onClick={handleUpload} className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-900 transition-colors">
                  Upload {files.length} file{files.length > 1 ? 's' : ''}
                </button>
              </div>
            )}

            {uploadError && <p className="mt-3 text-sm text-red-600">{uploadError}</p>}
          </div>
        )}

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <h2 className="text-sm font-medium text-black">Recent uploads</h2>
            <p className="text-xs text-gray-400 mt-0.5">Last {PAGE_SIZE} uploads per page.</p>
          </div>

          {uploadsLoading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading...</div>
          ) : uploads.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No uploads yet.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[400px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-2.5 px-4 font-medium text-gray-500">ID</th>
                      <th className="text-left py-2.5 px-4 font-medium text-gray-500">File</th>
                      <th className="text-left py-2.5 px-4 font-medium text-gray-500">Status</th>
                      <th className="text-left py-2.5 px-4 font-medium text-gray-500">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => handleRowClick(row)}
                        className={`border-b border-gray-100 hover:bg-gray-50 ${row.gemini_response != null || row.status === 'needs_review' ? 'cursor-pointer' : ''}`}
                      >
                        <td className="py-2 px-4 font-medium text-black">{row.id}</td>
                        <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                          {row.file ? (
                            <button type="button" onClick={() => handleViewFile(row.file)} disabled={viewingFile === row.file} className="text-sm text-gray-600 underline underline-offset-2 hover:text-black disabled:opacity-50 disabled:cursor-wait">
                              {viewingFile === row.file ? 'Opening...' : row.file}
                            </button>
                          ) : <span className="text-gray-400">–</span>}
                        </td>
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${statusBadge(row.status)}`}>
                            {row.status === 'needs_review' ? 'Needs review' : (row.status || 'pending')}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-gray-500">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
                  <p className="text-xs text-gray-400">Page {currentPage + 1} of {totalPages} ({uploadsTotal} total)</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={!canPrev} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Previous</button>
                    <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={!canNext} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {geminiModalRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setGeminiModalRow(null)} role="dialog" aria-modal="true">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white border border-gray-200 rounded-md shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-black">Gemini response – Invoice #{geminiModalRow.id}</h2>
                <button type="button" onClick={() => setGeminiModalRow(null)} className="text-gray-400 hover:text-black transition-colors" aria-label="Close">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto p-5">
                <pre className="text-xs text-gray-700 bg-gray-50 rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(geminiModalRow.gemini_response, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}

        {reviewRow && reviewForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeReviewModal} role="dialog" aria-modal="true">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white border border-gray-200 rounded-md shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-black">Review invoice #{reviewRow.id}</h2>
                <div className="flex items-center gap-2">
                  {reviewRow.file && (
                    <button type="button" onClick={() => handleViewFile(reviewRow.file)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">View PDF</button>
                  )}
                  <button type="button" onClick={closeReviewModal} className="text-gray-400 hover:text-black transition-colors" aria-label="Close">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-5 space-y-4">
                {reviewRow.gemini_response?.status && reviewRow.gemini_response.status !== 'ok' && (
                  <p className="text-sm text-gray-600 border border-gray-200 rounded-md px-3 py-2">
                    Gemini reported: <span className="font-medium">{reviewRow.gemini_response.status}</span>
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'date', label: 'Date', type: 'date', field: 'date' },
                    { id: 'invoice_number', label: 'Invoice number', type: 'text', field: 'invoice_number' },
                    { id: 'organization_name', label: 'Organization name', type: 'text', field: 'organization_name' },
                    { id: 'organization_number', label: 'Org. number', type: 'text', field: 'organization_number' },
                  ].map(({ id, label, type, field }) => (
                    <div key={id}>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                      <input type={type} value={reviewForm[field]} onChange={(e) => updateReviewField(field, e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors" />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Store name</label>
                    <input type="text" value={reviewForm.store_name} onChange={(e) => updateReviewField('store_name', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 mb-2">Voucher lines</h3>
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Line</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-gray-500">Amount (NOK)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'revenue', label: 'Revenue' },
                          { key: 'fees', label: 'Fees' },
                          { key: 'payout', label: 'Payout' },
                          { key: 'round_off', label: 'Round-off' },
                        ].map(({ key, label }) => (
                          <tr key={key} className="border-t border-gray-100">
                            <td className="py-2 px-3 text-gray-700">{label}</td>
                            <td className="py-2 px-3">
                              <input type="number" step="0.01" value={reviewForm[key]} onChange={(e) => updateReviewField(key, e.target.value)} className="w-full text-right border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-black transition-colors" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td className="py-2 px-3 font-semibold text-black">Balance</td>
                          <td className={`py-2 px-3 text-right font-semibold ${reviewBalances ? 'text-gray-700' : 'text-red-600'}`}>
                            {reviewBalance.toFixed(2)}{reviewBalances ? ' (OK)' : ' (does not balance)'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {approveError && <p className="text-sm text-red-600">{approveError}</p>}
              </div>

              <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
                <button type="button" onClick={closeReviewModal} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="button" onClick={handleApprove} disabled={!reviewBalances || approving || !reviewForm.date || !reviewForm.store_name} className="px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-900 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors">
                  {approving ? 'Approving...' : 'Approve & create voucher'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodoraUploadPage;
